import { useState, useRef, useEffect, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';

interface QuickLocation {
  name: string;
  coordinates: [number, number];
}

interface RouteSegment {
  fromIndex: number;
  toIndex: number;
  distance: number;
  duration: number;
  isApproximate: boolean;
  geometry: [number, number][];
  trainRoute?: {
    geometry: [number, number][];
    distance: number;
    stations: {
      from: {
        name: string;
        coordinates: [number, number];
        distanceFromPlace: number;
      };
      to: {
        name: string;
        coordinates: [number, number];
        distanceFromPlace: number;
      };
    };
  };
}

type TransportMode = 'car' | 'bus' | 'train';

interface UseTripEndLocationParams {
  mapRef: React.RefObject<mapboxgl.Map | null>;
  mapLoaded: boolean;
  tripEndFromLocation: QuickLocation | null;
  calculateDistance: (lat1: number, lon1: number, lat2: number, lon2: number) => number;
  fetchDrivingRoute: (from: [number, number], to: [number, number], signal?: AbortSignal) => Promise<any>;
  apiBaseUrl: string;
}

/**
 * Custom hook to manage trip end location state, marker, and route calculation
 * 
 * Extracted from Plan.tsx to reduce file size and isolate trip end logic.
 * 
 * @param params - Configuration parameters for trip end management
 * @returns Trip end location state, functions, and refs
 */
export function useTripEndLocation(params: UseTripEndLocationParams) {
  const { mapRef, mapLoaded, tripEndFromLocation, calculateDistance, fetchDrivingRoute, apiBaseUrl } = params;
  // Trip end location state
  const [isTripEnded, setIsTripEnded] = useState(false);
  const [tripEndLocation, setTripEndLocation] = useState<QuickLocation | null>(null);
  const [tripEndRouteSegment, setTripEndRouteSegment] = useState<RouteSegment | null>(null);
  const [tripEndTransportMode, setTripEndTransportMode] = useState<TransportMode>('car');
  const [tripEndTrainAvailable, setTripEndTrainAvailable] = useState(false);
  
  // Refs
  const tripEndMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const tripEndRouteAbortControllerRef = useRef<AbortController | null>(null);

  /**
   * Add trip end marker to map with red X icon
   */
  const addTripEndMarker = useCallback((coordinates: [number, number]) => {
    if (!mapRef.current) return;
    
    // Remove existing trip end marker if any
    if (tripEndMarkerRef.current) {
      tripEndMarkerRef.current.remove();
    }
    
    // Create custom marker element with X inside
    const el = document.createElement('div');
    el.innerHTML = `
      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="filter: drop-shadow(0 3px 6px rgba(0,0,0,0.4));">
        <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" fill="#ef4444" stroke="#dc2626" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <line x1="9" y1="7" x2="15" y2="13" stroke="white" stroke-width="2" stroke-linecap="round"/>
        <line x1="15" y1="7" x2="9" y2="13" stroke="white" stroke-width="2" stroke-linecap="round"/>
      </svg>
    `;
    el.style.width = '36px';
    el.style.height = '36px';
    el.style.cursor = 'pointer';
    
    // Create marker
    tripEndMarkerRef.current = new mapboxgl.Marker({
      element: el,
      anchor: 'bottom'
    })
      .setLngLat(coordinates)
      .addTo(mapRef.current);
  }, [mapRef]);

  /**
   * Remove trip end marker from map
   */
  const removeTripEndMarker = useCallback(() => {
    if (tripEndMarkerRef.current) {
      tripEndMarkerRef.current.remove();
      tripEndMarkerRef.current = null;
    }
  }, []);

  // Manage trip end marker based on tripEndLocation
  useEffect(() => {
    if (!mapLoaded) return;
    
    if (tripEndLocation && tripEndLocation.coordinates) {
      addTripEndMarker(tripEndLocation.coordinates as [number, number]);
    } else {
      removeTripEndMarker();
    }
    
    // Cleanup on unmount
    return () => {
      removeTripEndMarker();
    };
  }, [tripEndLocation, mapLoaded, addTripEndMarker, removeTripEndMarker]);

  // Calculate trip end route when trip end location is set
  useEffect(() => {
    if (!isTripEnded || !tripEndLocation) {
      return;
    }

    // Abort any previous request
    if (tripEndRouteAbortControllerRef.current) {
      tripEndRouteAbortControllerRef.current.abort();
    }

    // Create a new AbortController for this calculation
    const abortController = new AbortController();
    tripEndRouteAbortControllerRef.current = abortController;

    const calculateTripEndRoute = async () => {
      const fromLocation = tripEndFromLocation;
      
      if (!fromLocation) {
        return;
      }

      // Calculate crow fly distance
      const crowFlyDistance = calculateDistance(
        fromLocation.coordinates[1], // lat
        fromLocation.coordinates[0], // lng
        tripEndLocation.coordinates[1], // lat
        tripEndLocation.coordinates[0] // lng
      );

      let segment: RouteSegment;

      if (crowFlyDistance < 10) {
        // Use straight line for distances < 10km
        const adjustedDistance = crowFlyDistance * 1.2;
        const duration = Math.round((adjustedDistance / 30) * 60); // minutes

        segment = {
          fromIndex: 0,
          toIndex: 1,
          distance: crowFlyDistance,
          duration,
          isApproximate: true,
          geometry: [fromLocation.coordinates, tripEndLocation.coordinates]
        };
      } else {
        // Fetch driving route from Mapbox for distances >= 10km
        try {
          if (abortController.signal.aborted) {
            return;
          }
          const route = await fetchDrivingRoute(fromLocation.coordinates, tripEndLocation.coordinates, abortController.signal);
          segment = {
            fromIndex: 0,
            toIndex: 1,
            distance: route.distance,
            duration: route.duration,
            isApproximate: false,
            geometry: route.geometry
          };
        } catch (error) {
          // Fallback to straight line
          segment = {
            fromIndex: 0,
            toIndex: 1,
            distance: crowFlyDistance,
            duration: Math.round((crowFlyDistance / 30) * 60),
            isApproximate: true,
            geometry: [fromLocation.coordinates, tripEndLocation.coordinates]
          };
        }
      }

      setTripEndRouteSegment(segment);

      // Check train availability
      try {
        if (abortController.signal.aborted) {
          return;
        }

        const from = { 
          lat: fromLocation.coordinates[1], 
          lng: fromLocation.coordinates[0] 
        };
        const to = { 
          lat: tripEndLocation.coordinates[1], 
          lng: tripEndLocation.coordinates[0] 
        };
        
        const response = await fetch(`${apiBaseUrl}/api/advanced-trip/train-route`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ from, to }),
          signal: abortController.signal
        });
        const result = await response.json();
        
        if (result.available && result.route) {
          setTripEndTrainAvailable(true);
          setTripEndRouteSegment(prev => prev ? { ...prev, trainRoute: result.route } : null);
        } else {
          setTripEndTrainAvailable(false);
        }
      } catch (error) {
        setTripEndTrainAvailable(false);
      }
    };

    calculateTripEndRoute();
  }, [isTripEnded, tripEndLocation, tripEndFromLocation, calculateDistance, fetchDrivingRoute, apiBaseUrl]);

  return {
    // State
    isTripEnded,
    setIsTripEnded,
    tripEndLocation,
    setTripEndLocation,
    tripEndRouteSegment,
    setTripEndRouteSegment,
    tripEndTransportMode,
    setTripEndTransportMode,
    tripEndTrainAvailable,
    setTripEndTrainAvailable,
    
    // Ref
    tripEndMarkerRef,
    
    // Functions
    addTripEndMarker,
    removeTripEndMarker,
  };
}

